import { NextApiRequest, NextApiResponse } from 'next'
import Cookies from 'cookie-universal'
import { IncomingMessage, ServerResponse } from 'http'
import { BRAND_KEY } from '@educator/constants'
import { paths } from './paths'

// Cookie names
export const COOKIE_NAMES = {
  ACCESS_TOKEN: `${BRAND_KEY}_access_token`,
  ID_TOKEN: `${BRAND_KEY}_id_token`,
  REFRESH_TOKEN: `${BRAND_KEY}_refresh_token`,
  PENDING_EMAIL: `${BRAND_KEY}_pending_email`,
  USER_TYPE: `${BRAND_KEY}_user_type`,
} as const

// User type constants
export const USER_TYPE = {
  NEW: 'new',
  EXISTING: 'existing',
} as const

// User type labels for analytics
export const USER_TYPE_LABEL = {
  NEW: 'new user',
  EXISTING: 'existing user',
} as const

// Pending email cookie expiration: 10 minutes (matches OAuth state cookies)
const PENDING_EMAIL_MAX_AGE = 600

// Append, never replace: _app writes tracking cookies earlier in the same response.
export const appendSetCookie = (
  res: ServerResponse,
  cookiesToSet: string[],
) => {
  const existing = res.getHeader('Set-Cookie')
  const prior = existing === undefined ? [] : [existing].flat().map(String)
  res.setHeader('Set-Cookie', [...prior, ...cookiesToSet])
}

/**
 * Set authentication cookies
 */
export const setAuthCookies = ({
  res,
  accessToken,
  idToken,
  refreshToken,
  expiresIn,
}: {
  res: NextApiResponse
  accessToken: string
  idToken?: string
  refreshToken?: string
  expiresIn?: number
}) => {
  const cookies = Cookies()
  const expires = new Date(Date.now() + (expiresIn || 3600) * 1000) // expiresIn in seconds
  const isSecure = process.env.NODE_ENV === 'production'
  const properties = {
    expires,
    secure: isSecure,
    httpOnly: true,
    path: '/',
    sameSite: 'lax',
  } as const

  const cookiesToSet = [
    cookies.nodeCookie.serialize(
      COOKIE_NAMES.ACCESS_TOKEN,
      accessToken,
      properties,
    ),
  ]

  if (idToken) {
    cookiesToSet.push(
      cookies.nodeCookie.serialize(COOKIE_NAMES.ID_TOKEN, idToken, properties),
    )
  }

  if (refreshToken) {
    // Refresh token expiration: Auth0 doesn't return this in the response,
    // so we use the configured value from Auth0 dashboard (30 days = 2592000 seconds)
    const REFRESH_TOKEN_LIFETIME = 2592000 // 30 days - matches Auth0 config
    const refreshProperties = {
      ...properties,
      expires: new Date(Date.now() + REFRESH_TOKEN_LIFETIME * 1000),
    }
    cookiesToSet.push(
      cookies.nodeCookie.serialize(
        COOKIE_NAMES.REFRESH_TOKEN,
        refreshToken,
        refreshProperties,
      ),
    )
  }

  res.setHeader('Access-Control-Allow-Credentials', 'true')
  appendSetCookie(res, cookiesToSet)
}

/**
 * Update the in-memory request cookie header so reads later in the SAME request
 * observe freshly-refreshed tokens.
 *
 * `setAuthCookies` only writes `Set-Cookie` on the response — `req.headers.cookie`
 * still carries the pre-refresh (expired) access token for the rest of the SSR /
 * API pass. Any same-request consumer that re-reads the token from `req` (e.g.
 * `getMcUserIdFromCtx`, which feeds GrowthBook attributes) would otherwise verify
 * an expired token, silently drop `mcUserId`, and misbucket mcUserId-targeted
 * flags. Patch `req` so it stays coherent with what we just wrote to `res`.
 */
export const syncRequestAuthCookies = ({
  req,
  accessToken,
  idToken,
  refreshToken,
}: {
  req: IncomingMessage
  accessToken: string
  idToken?: string
  refreshToken?: string
}) => {
  const { nodeCookie } = Cookies()
  const parsed = nodeCookie.parse(req.headers.cookie || '')
  parsed[COOKIE_NAMES.ACCESS_TOKEN] = accessToken
  if (idToken) parsed[COOKIE_NAMES.ID_TOKEN] = idToken
  if (refreshToken) parsed[COOKIE_NAMES.REFRESH_TOKEN] = refreshToken
  req.headers.cookie = Object.entries(parsed)
    .map(([name, value]) => nodeCookie.serialize(name, value as string))
    .join('; ')
}

/**
 * Clear authentication cookies
 */
export const clearAuthCookies = (res: NextApiResponse) => {
  const cookies = Cookies()
  const isSecure = process.env.NODE_ENV === 'production'
  const properties = {
    expires: new Date(0),
    httpOnly: true,
    path: '/',
    sameSite: 'lax',
    secure: isSecure,
  } as const

  res.setHeader('Access-Control-Allow-Credentials', 'true')
  appendSetCookie(res, [
    cookies.nodeCookie.serialize(COOKIE_NAMES.ACCESS_TOKEN, '', properties),
    cookies.nodeCookie.serialize(COOKIE_NAMES.ID_TOKEN, '', properties),
    cookies.nodeCookie.serialize(COOKIE_NAMES.REFRESH_TOKEN, '', properties),
  ])
}

/**
 * Set the pending email cookie during registration flow.
 * This stores the validated email from the email-entry page
 * so it can be read by register/login pages without URL params.
 */
export const setPendingEmailCookie = (res: NextApiResponse, email: string) => {
  const cookies = Cookies()
  const isSecure = process.env.NODE_ENV === 'production'
  const properties = {
    maxAge: PENDING_EMAIL_MAX_AGE,
    httpOnly: true,
    secure: isSecure,
    path: '/',
    sameSite: 'lax',
  } as const

  const cookie = cookies.nodeCookie.serialize(
    COOKIE_NAMES.PENDING_EMAIL,
    email,
    properties,
  )

  // Append to existing Set-Cookie headers if any
  const existingCookies = res.getHeader('Set-Cookie')
  if (existingCookies) {
    const cookiesArray = Array.isArray(existingCookies)
      ? existingCookies
      : [existingCookies as string]
    res.setHeader('Set-Cookie', [...cookiesArray, cookie])
  } else {
    res.setHeader('Set-Cookie', cookie)
  }
}

/**
 * Get the pending email from the cookie.
 * Returns the email string or null if not found.
 */
export const getPendingEmailCookie = (
  req: NextApiRequest | Parameters<typeof Cookies>[0],
  res?: NextApiResponse | Parameters<typeof Cookies>[1],
): string | null => {
  const cookies = Cookies(req, res)
  const email = cookies.get(COOKIE_NAMES.PENDING_EMAIL)
  return typeof email === 'string' && email.length > 0 ? email : null
}

/**
 * Clear the pending email cookie after successful auth.
 */
export const clearPendingEmailCookie = (res: NextApiResponse) => {
  const cookies = Cookies()
  const isSecure = process.env.NODE_ENV === 'production'
  const properties = {
    expires: new Date(0),
    httpOnly: true,
    path: '/',
    sameSite: 'lax',
    secure: isSecure,
  } as const

  const cookie = cookies.nodeCookie.serialize(
    COOKIE_NAMES.PENDING_EMAIL,
    '',
    properties,
  )

  // Append to existing Set-Cookie headers if any
  const existingCookies = res.getHeader('Set-Cookie')
  if (existingCookies) {
    const cookiesArray = Array.isArray(existingCookies)
      ? existingCookies
      : [existingCookies as string]
    res.setHeader('Set-Cookie', [...cookiesArray, cookie])
  } else {
    res.setHeader('Set-Cookie', cookie)
  }
}

// User type cookie expiration: 30 days (matches refresh token lifetime)
const USER_TYPE_MAX_AGE = 2592000 // 30 days

/**
 * Set the user type cookie to mark a user as 'new' during registration flow.
 * This cookie persists for 30 days to track new users across their initial sessions.
 */
export const setUserTypeCookie = (res: NextApiResponse | ServerResponse) => {
  const cookies = Cookies()
  const isSecure = process.env.NODE_ENV === 'production'
  const properties = {
    maxAge: USER_TYPE_MAX_AGE,
    httpOnly: true,
    secure: isSecure,
    path: '/',
    sameSite: 'lax',
  } as const

  const cookie = cookies.nodeCookie.serialize(
    COOKIE_NAMES.USER_TYPE,
    USER_TYPE.NEW,
    properties,
  )

  // Append to existing Set-Cookie headers if any
  const existingCookies = res.getHeader('Set-Cookie')
  if (existingCookies) {
    const cookiesArray = Array.isArray(existingCookies)
      ? existingCookies
      : [existingCookies as string]
    res.setHeader('Set-Cookie', [...cookiesArray, cookie])
  } else {
    res.setHeader('Set-Cookie', cookie)
  }
}

/**
 * Get the user type from the cookie.
 * Returns 'new' if the cookie exists, 'existing' otherwise (safe default).
 */
export const getUserTypeCookie = (
  req: NextApiRequest | Parameters<typeof Cookies>[0],
  res?: NextApiResponse | Parameters<typeof Cookies>[1],
): 'new' | 'existing' => {
  const cookies = Cookies(req, res)
  const userType = cookies.get(COOKIE_NAMES.USER_TYPE)
  return userType === USER_TYPE.NEW ? USER_TYPE.NEW : USER_TYPE.EXISTING
}

/**
 * Determine user type based on current pathname and cookie state.
 *
 * Logic:
 * - /register page → 'new' (immediate, no cookie read needed)
 * - /email or /login pages → 'existing' (immediate)
 * - Other pages → read from cookie (set in previous requests)
 *
 * This solves the timing issue where cookies set in middleware aren't
 * readable in the same request.
 */
export const determineUserType = (
  pathname: string,
  req: NextApiRequest | Parameters<typeof Cookies>[0],
  res?: NextApiResponse | Parameters<typeof Cookies>[1],
): 'new' | 'existing' => {
  if (pathname === paths.register) {
    return USER_TYPE.NEW
  }

  if (pathname === paths.email || pathname === paths.login) {
    return USER_TYPE.EXISTING
  }

  return getUserTypeCookie(req, res)
}

/**
 * Clear the user type cookie when user logs in or visits /email or /login page.
 */
export const clearUserTypeCookie = (res: NextApiResponse | ServerResponse) => {
  const cookies = Cookies()
  const isSecure = process.env.NODE_ENV === 'production'
  const properties = {
    expires: new Date(0),
    httpOnly: true,
    path: '/',
    sameSite: 'lax',
    secure: isSecure,
  } as const

  const cookie = cookies.nodeCookie.serialize(
    COOKIE_NAMES.USER_TYPE,
    '',
    properties,
  )

  // Append to existing Set-Cookie headers if any
  const existingCookies = res.getHeader('Set-Cookie')
  if (existingCookies) {
    const cookiesArray = Array.isArray(existingCookies)
      ? existingCookies
      : [existingCookies as string]
    res.setHeader('Set-Cookie', [...cookiesArray, cookie])
  } else {
    res.setHeader('Set-Cookie', cookie)
  }
}
